import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { getServiceNameWithNickname } from "../../../utils/getServiceNameWithNickname";
import RenderStatus from "../../styles/styledStatus";
import { TimestampToDateMoment } from "../../../utils/timestampToDate";
import { Alert } from "@material-ui/lab";
import PageSpinner from "../../elements/spinner/PageSpinner";
import { Tooltip } from "@material-ui/core";
import Notification from "../../elements/notification/Notification";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";

import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../styles/styledTable";
import { StyledTooltip } from "../../styles/styledTooltip";
import { StyledCopy } from "../../styles/styledCopy";
import { StyledBreakWord } from "../../styles/styledBreakWord";
import { useTranslation } from "react-i18next";

const PayoutList = ({ payouts }) => {
  const { t } = useTranslation("payoutsPage");
  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  if (!payouts) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (payouts.length === 0) {
    return <Alert severity="warning">{t("table.emptyData")}</Alert>;
  }

  const showDetails = (e) => {
    if (e.target.closest(".stop-event")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledScrollTable className="payout-table_scroll">
        <StyledTable
          width="2750"
          size="14"
          className="payout-table"
        >
          <StyledTableHead
            scroll="auto"
            col="12"
            className="payout-table__head"
          >
            <div>
              Id
              <Tooltip title={t("table.tooltips.id")} arrow placement="top">
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              Reference id
              <Tooltip title={t("table.tooltips.referenceId")} arrow placement="top">
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("table.project")}
            </div>
            <div>
              {t("table.service")}
            </div>
            <div>
              {t("table.paymentSystem")}
            </div>
            <div>
              {t("table.currency")}
            </div>
            <div>
              {t("table.amount")}
              <Tooltip title={t("table.tooltips.amount")} arrow placement="top">
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("table.processedAmount")}
              <Tooltip title={t("table.tooltips.processedAmount")} arrow placement="top">
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("table.status")}
            </div>
            <div>
              {t("table.createdAt")}
            </div>
            <div>
              {t("table.updatedAt")}
            </div>
            <div>
              {t("table.paymentType")}
            </div>
            <div>
              {t("table.transactionType")}
            </div>
            <div>
              Related reference id
            </div>
            <div>
              {t("table.note")}
            </div>
          </StyledTableHead>
          <StyledTableBody>
            {payouts && payouts.map((value, key) => (
              <StyledTableRow
                key={key}
                scroll="auto"
                col="12"
                className="payout-table__row"
                onClick={(e) => {showDetails(e);}}
                cursor="pointer"
                as={NavLink}
                to={`/payouts/${value.id}`}
              >
                <StyledCol inline data-title="id">
                  <CopyToClipboard
                    className="stop-event"
                    text={value.id}
                    onCopy={() => {
                      setNotification({ ...notification, visible: true, type: "success", message: t("table.copied") });
                    }}
                  >
                    <StyledBreakWord as="p">
                      {value.id} <StyledCopy title={t("table.copy")} className="icon-light-copy" />
                    </StyledBreakWord>
                  </CopyToClipboard>
                </StyledCol>
                <StyledCol inline data-title="Reference id">
                  <CopyToClipboard
                    className="stop-event"
                    text={value.referenceId}
                    onCopy={() => {
                      setNotification({ ...notification, visible: true, type: "success", message: t("table.copied") });
                    }}
                  >
                    <StyledBreakWord as="p">
                      {value.referenceId} <StyledCopy title={t("table.copy")} className="icon-light-copy" />
                    </StyledBreakWord>
                  </CopyToClipboard>
                </StyledCol>
                <StyledCol data-title={t("table.project")}>
                  {value.connection.project.name}
                </StyledCol>
                <StyledCol data-title={t("table.service")}>
                  {getServiceNameWithNickname(value.connection.service.name, value.connection.service.nickname)}
                </StyledCol>
                <StyledCol data-title={t("table.paymentSystem")}>
                  {value.paymentSystem.name}
                </StyledCol>
                <StyledCol data-title={t("table.currency")}>
                  {value.currency.asset}
                </StyledCol>
                <StyledCol data-title={t("table.amount")}>
                  {value.amount}
                </StyledCol>
                <StyledCol data-title={t("table.processedAmount")}>
                  {value.currency.type === "CRYPTO" ? value.amount : value.processedAmount}
                </StyledCol>
                <StyledCol inline data-title={t("table.status")}>
                  <RenderStatus status={value.status.value} />
                </StyledCol>
                <StyledCol data-title={t("table.createdAt")}>
                  {TimestampToDateMoment(value.createdAt)}
                </StyledCol>
                <StyledCol data-title={t("table.updatedAt")}>
                  {TimestampToDateMoment(value.updatedAt)}
                </StyledCol>
                <StyledCol data-title={t("table.paymentType")}>
                  {value.paymentType}
                </StyledCol>
                <StyledCol data-title={t("table.transactionType")}>
                  {value.transactionType}
                </StyledCol>
                <StyledCol data-title="Related reference id">
                  {value.relatedReferenceId}
                </StyledCol>
                <StyledCol inline data-title={t("table.note")} className="payment-table__note">
                  <p className="note">
                    {value.note}
                  </p>
                  {value.note && value.note.length > 25 &&
                    <Tooltip
                      title={value.note}
                      arrow
                      placement="top"
                    >
                      <StyledTooltip className="icon-light-info" />
                    </Tooltip>}
                </StyledCol>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledScrollTable>
    </>
  );
};

export default PayoutList;